import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import type { AuditList } from "@/types";

export interface AuditFilters {
  action?: string;
  actor?: string;
  from?: string; // ISO timestamp
  to?: string;
  limit?: number;
  offset?: number;
}

function buildAuditParams(filters: AuditFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.action) params.set("action", filters.action);
  if (filters.actor) params.set("actor", filters.actor);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset !== undefined) params.set("offset", String(filters.offset));
  return params;
}

export function useAuditLog(filters: AuditFilters = {}) {
  return useQuery({
    queryKey: ["audit", "list", filters],
    queryFn: async () => {
      const params = buildAuditParams({
        ...filters,
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0,
      });
      const { data } = await api.get<AuditList>(
        `/api/v1/audit?${params.toString()}`,
      );
      return data;
    },
  });
}

// S12 — actions dropdown source.
export function useAuditActions() {
  return useQuery({
    queryKey: ["audit", "actions"],
    queryFn: async () => {
      const { data } = await api.get<string[]>("/api/v1/audit/actions");
      return data;
    },
    staleTime: 60_000,
  });
}

// S12 — CSV export. Returns a Blob the caller turns into a download.
export async function exportAuditCsv(filters: AuditFilters): Promise<Blob> {
  const params = buildAuditParams(filters);
  const { data } = await api.get<Blob>(
    `/api/v1/audit/export?${params.toString()}`,
    { responseType: "blob" },
  );
  return data;
}
